import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { Clock, Mail, Shield, CheckCircle } from 'lucide-react';

const RequestSubmittedPage = () => {
  const { t } = useTranslation();

  return (
    <div className="h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 overflow-y-auto ">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('requestSubmitted.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('requestSubmitted.subtitle')}
          </p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Step 1: Request Submitted */}
          <Card className="p-6 text-center border-2 border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700">
            <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
              {t('requestSubmitted.step1.title')}
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {t('requestSubmitted.step1.description')}
            </p>
          </Card>

          {/* Step 2: Under Review */}
          <Card className="p-6 text-center border-2 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              {t('requestSubmitted.step2.title')}
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {t('requestSubmitted.step2.description')}
            </p>
          </Card>

          {/* Step 3: Approval */}
          <Card className="p-6 text-center border-2 border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
              {t('requestSubmitted.step3.title')}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('requestSubmitted.step3.description')}
            </p>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="p-8 bg-white dark:bg-neutral-900">
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto">
              <Clock className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {t('requestSubmitted.waiting.title')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                {t('requestSubmitted.waiting.description')}
              </p>
            </div>

            {/* What happens next */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-left">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('requestSubmitted.whatHappensNext.title')}
              </h3>
              <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('requestSubmitted.whatHappensNext.step1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('requestSubmitted.whatHappensNext.step2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('requestSubmitted.whatHappensNext.step3')}</span>
                </li>
              </ul>
            </div>

            {/* Contact info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {t('requestSubmitted.contact.title')}
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('requestSubmitted.contact.description')}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RequestSubmittedPage;
